import { Sector, MissionScenario } from '../types/game';

export const SECTOR_POSITIONS: Record<string, { top: string, left: string }> = {
  'S009': { top: '50%', left: '50%' }, // Leadership (Center)
  'S002': { top: '65%', left: '65%' }, // Finance
  'S001': { top: '20%', left: '20%' }, // Defense
  'S006': { top: '35%', left: '35%' }, // Public Safety
  'S003': { top: '80%', left: '20%' }, // Healthcare
  'S005': { top: '20%', left: '80%' }, // Education
  'S007': { top: '80%', left: '80%' }, // Manufacturing
  'S004': { top: '50%', left: '85%' }, // Transportation
  'S008': { top: '85%', left: '50%' }  // Agriculture
};

export const INITIAL_SECTORS: Sector[] = [
  {
    id: 'S001',
    name: 'Defense',
    type: 'Defense',
    description: 'Military command, strategic operations, defense research. Key locations: Pentagon equivalent, military bases, defense contractor HQs.',
    trustScore: 8,
    maxTrust: 10,
    owner: 'Blue',
    coi: true,
    risks: [
      { id: 'r3', name: 'Infiltration', description: 'Suspected enemy agents on base.', severity: 'high', active: false },
      { id: 'r11', name: 'Sabotage', description: 'Runway lights tampered with.', severity: 'high', active: false }
    ],
    dependencies: ['S009', 'S007', 'S004'],
    actionHistory: [],
    color: 'slate'
  },
  {
    id: 'S002',
    name: 'Finance',
    type: 'Finance',
    description: 'Banking, markets, financial systems, economic infrastructure. Key locations: Federal Reserve equivalent, stock exchange, major banks.',
    trustScore: 6,
    maxTrust: 10,
    owner: 'Neutral',
    coi: true,
    risks: [
      { id: 'r1', name: 'Market Panic', description: 'Rumors of insolvency spreading.', severity: 'high', active: false },
      { id: 'r9', name: 'Data Breach', description: 'Sensitive user data exposed.', severity: 'high', active: false }
    ],
    dependencies: ['S009', 'S007', 'S008'],
    actionHistory: [],
    color: 'emerald'
  },
  {
    id: 'S003',
    name: 'Healthcare',
    type: 'Healthcare',
    description: 'Medical systems, hospitals, pharmaceuticals, public health. Key locations: Teaching hospitals, research centers, CDC equivalent.',
    trustScore: 6,
    maxTrust: 10,
    owner: 'Neutral',
    coi: false,
    risks: [
      { id: 'r12', name: 'Bio-Threat', description: 'Unknown pathogen reported.', severity: 'high', active: false }
    ],
    dependencies: ['S006', 'S004', 'S007'],
    actionHistory: [],
    color: 'rose'
  },
  {
    id: 'S004',
    name: 'Transportation',
    type: 'Transportation',
    description: 'Aviation, maritime, rail, roads, logistics. Key locations: Airports, ports, railway hubs, DOT equivalent.',
    trustScore: 5,
    maxTrust: 10,
    owner: 'Neutral',
    coi: false,
    risks: [
      { id: 'r4', name: 'Gridlock', description: 'Cyberattack on traffic signals.', severity: 'medium', active: false }
    ],
    dependencies: ['S002', 'S006'],
    actionHistory: [],
    color: 'cyan'
  },
  {
    id: 'S005',
    name: 'Education',
    type: 'Education',
    description: 'Universities, schools, research institutions, cultural centers. Key locations: State university, national library, think tanks.',
    trustScore: 7,
    maxTrust: 10,
    owner: 'Neutral',
    coi: false,
    risks: [
      { id: 'r5', name: 'Protests', description: 'Student demonstrations escalating.', severity: 'medium', active: false }
    ],
    dependencies: ['S009', 'S006'],
    actionHistory: [],
    color: 'amber'
  },
  {
    id: 'S006',
    name: 'Public Safety',
    type: 'Public Safety',
    description: 'Police, fire, emergency services, disaster response. Key locations: Police HQ, EOC, fire stations.',
    trustScore: 6,
    maxTrust: 10,
    owner: 'Neutral',
    coi: true,
    risks: [
      { id: 'r6', name: 'Corruption', description: 'Police chief implicated in scandal.', severity: 'high', active: false }
    ],
    dependencies: ['S009', 'S001'],
    actionHistory: [],
    color: 'blue'
  },
  {
    id: 'S007',
    name: 'Manufacturing',
    type: 'Manufacturing',
    description: 'Industry, factories, supply chain, raw materials. Key locations: Industrial park, refineries, logistics centers.',
    trustScore: 5,
    maxTrust: 10,
    owner: 'Neutral',
    coi: false,
    risks: [
      { id: 'r7', name: 'Strike', description: 'Workers union threatening walkout.', severity: 'medium', active: false }
    ],
    dependencies: ['S002', 'S004', 'S008'],
    actionHistory: [],
    color: 'orange'
  },
  {
    id: 'S008',
    name: 'Agriculture',
    type: 'Agriculture',
    description: 'Farming, food supply, water resources, rural communities. Key locations: Major farms, grain silos, water treatment.',
    trustScore: 6,
    maxTrust: 10,
    owner: 'Neutral',
    coi: false,
    risks: [
      { id: 'r8', name: 'Drought', description: 'Water scarcity causing unrest.', severity: 'medium', active: false }
    ],
    dependencies: ['S007', 'S004'],
    actionHistory: [],
    color: 'lime'
  },
  {
    id: 'S009',
    name: 'Leadership',
    type: 'Leadership',
    description: 'Government, political figures, national administration. Key locations: Capitol building, executive mansion, ministries.',
    trustScore: 7,
    maxTrust: 10,
    owner: 'Neutral',
    coi: true,
    risks: [
      { id: 'r10', name: 'Scandal', description: 'High-level corruption allegations.', severity: 'high', active: false }
    ],
    dependencies: ['S001', 'S002', 'S006'],
    actionHistory: [],
    color: 'violet'
  }
];

export const SCENARIOS: MissionScenario[] = [
  {
    id: 'm1',
    name: 'Operation: First Light',
    description: 'Stabilize the Defense and Finance sectors amidst rising tensions.',
    objectives: ['Secure Defense Sector (Trust > 8)', 'Stabilize Finance Sector (Trust > 6)'],
    targetSectorIds: ['S001', 'S002'],
    turnLimit: 8,
    startingTrust: 5,
    difficulty: 'Easy'
  },
  {
    id: 'm2',
    name: 'Operation: Iron Shield',
    description: 'Protect critical infrastructure from coordinated cyber attacks.',
    objectives: ['Maintain Transportation Trust > 5', 'Secure Manufacturing Sector (Trust > 7)'],
    targetSectorIds: ['S004', 'S007'],
    turnLimit: 10,
    startingTrust: 4,
    difficulty: 'Medium'
  },
  {
    id: 'm3',
    name: 'Operation: Silent Storm',
    description: 'Counter a massive disinformation campaign targeting Leadership and Public Safety.',
    objectives: ['Restore Leadership Trust > 6', 'Prevent Civil Unrest in Public Safety'],
    targetSectorIds: ['S009', 'S006'],
    turnLimit: 12,
    startingTrust: 3,
    difficulty: 'Hard'
  },
  // Tutorial Scenarios
  {
    id: 'tutorial-opsec',
    name: 'Training: Operations Security',
    description: 'Learn to protect critical information. Use OPSEC capabilities to secure the Defense sector.',
    objectives: ['Secure Defense Sector (Trust > 9)'],
    targetSectorIds: ['S001'],
    turnLimit: 5,
    startingTrust: 6,
    difficulty: 'Easy'
  },
  {
    id: 'tutorial-pa',
    name: 'Training: Public Affairs',
    description: 'Learn to inform audiences. Use Public Affairs to boost trust in the Finance sector.',
    objectives: ['Stabilize Finance Sector (Trust > 8)'],
    targetSectorIds: ['S002'],
    turnLimit: 5,
    startingTrust: 5,
    difficulty: 'Easy'
  },
  {
    id: 'tutorial-intel',
    name: 'Training: Intelligence',
    description: 'Gain situational awareness. Use ISR to uncover risks in the Leadership sector.',
    objectives: ['Identify all Risks in Leadership'],
    targetSectorIds: ['S009'],
    turnLimit: 5,
    startingTrust: 5,
    difficulty: 'Easy'
  },
  {
    id: 'tutorial-ew',
    name: 'Training: Electronic Warfare',
    description: 'Disrupt adversary communications. Use Jamming to neutralize threats in the Transportation sector.',
    objectives: ['Neutralize Risks in Transportation'],
    targetSectorIds: ['S004'],
    turnLimit: 5,
    startingTrust: 5,
    difficulty: 'Easy'
  },
  {
    id: 'tutorial-tech',
    name: 'Training: Technical Activities',
    description: 'Deploy sensors and cyber capabilities. Secure the Manufacturing sector against cyber threats.',
    objectives: ['Secure Manufacturing (Trust > 8)'],
    targetSectorIds: ['S007'],
    turnLimit: 5,
    startingTrust: 5,
    difficulty: 'Easy'
  },
  {
    id: 'tutorial-decoy',
    name: 'Training: Deceptive Activities',
    description: 'Mislead the adversary. Use Decoys to protect the Agriculture sector.',
    objectives: ['Maintain Agriculture Trust > 6'],
    targetSectorIds: ['S008'],
    turnLimit: 5,
    startingTrust: 5,
    difficulty: 'Easy'
  },
  {
    id: 'tutorial-psyop',
    name: 'Training: Psychological Operations',
    description: 'Influence target audiences. Use PSYOP to shift sentiment in the Education sector.',
    objectives: ['Win Hearts and Minds in Education (Trust > 8)'],
    targetSectorIds: ['S005'],
    turnLimit: 5,
    startingTrust: 5,
    difficulty: 'Easy'
  },
  {
    id: 'combined-arms-1',
    name: 'Combined Arms: Operation Unity',
    description: 'Integrate multiple capabilities to secure a region. Coordinate OPSEC, PA, and Cyber.',
    objectives: ['Secure Defense and Finance', 'Neutralize all Risks'],
    targetSectorIds: ['S001', 'S002'],
    turnLimit: 10,
    startingTrust: 4,
    difficulty: 'Medium'
  },
  {
    id: 'full-spectrum-1',
    name: 'Full Spectrum: Total Dominance',
    description: 'Mastery level operation. Utilize all available capabilities to achieve strategic victory.',
    objectives: ['Secure 5+ Sectors', 'Maintain Average Trust > 7'],
    targetSectorIds: ['S001', 'S002', 'S009', 'S006', 'S004'],
    turnLimit: 15,
    startingTrust: 3,
    difficulty: 'Hard'
  }
];
